import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
  Button,
  SimpleTabs,
  TranslationProvider,
  getTranslation,
  AriaLiveRegion,
} from '@resy/ui-kitchen';

import './CitiesList.scss';

const KEY = {
  ENTER: 'Enter', SPACE: ' ', ARROW_DOWN: 'ArrowDown', ARROW_UP: 'ArrowUp', ESCAPE: 'Escape',
};

const CitiesList = (props) => {
  const {
    allCities,
    locations,
    popular,
    selectedLocationName,
    onLocationSelect,
    searchVal,
    tabs,
    activeTab,
    onTabChange,
    tabsAriaLabel,
    showWineRegions,
  } = props;

  const [state, setState] = React.useState(null);

  const hasTabs = Array.isArray(tabs) && tabs.length > 0;

  // If there are tabs, find the currently active tab and use its locations.
  // If the active tab can't be found or has no locations, use an empty array.
  // If there are no tabs at all, use the top-level locations variable instead.
  const activeLocations = hasTabs
    ? (tabs.find((tab) => tab.id === activeTab)?.locations ?? [])
    : locations;

  const onListBlur = () => {
    setState(null);
  };

  const onItemFocus = (e) => {
    setState({ [e.target.id]: true });
  };

  const onItemKeyDown = (e, location) => {
    let elementToFocus;
    if (e.key === KEY.ARROW_DOWN) {
      if (e.currentTarget?.nextSibling) {
        elementToFocus = e.currentTarget?.nextSibling;
      } else if (e.currentTarget === e.currentTarget?.parentElement?.lastElementChild) {
        elementToFocus = e.currentTarget?.parentElement?.firstElementChild;
      }
    } else if (e.key === KEY.ARROW_UP) {
      if (e.currentTarget?.previousSibling) {
        elementToFocus = e.currentTarget?.previousSibling;
      } else if (e.currentTarget === e.currentTarget?.parentElement?.firstElementChild) {
        elementToFocus = e.currentTarget?.parentElement?.lastElementChild;
      }
    }

    if (elementToFocus) {
      elementToFocus.focus();
      return;
    }

    if (e.key === KEY.ESCAPE) {
      setState({});
      document.querySelector('#locationSearch').focus();
      return;
    }

    if (e.key === KEY.ENTER || e.key === KEY.SPACE) {
      onLocationSelect(location);
      e.preventDefault();
    }
  };

  const errorMsg = () => {
    if (searchVal !== '' && popular && !allCities) {
      return (
        <AriaLiveRegion offscreen={false}>
          <div className="CitiesList__error">
            <TranslationProvider
              translationKey="locations_search_error_title"
              translationValues={{ search: searchVal }}
            >
              <div className="CitiesList__error-title">
                {`No results for “${searchVal}”.`}
              </div>
            </TranslationProvider>
            <TranslationProvider
              translationKey={showWineRegions
                ? 'locations_search_error_desc_wine_regions'
                : 'locations_search_error_desc'}
            >
              <div className="CitiesList__error-desc">
                {showWineRegions
                  ? 'Try another search, or select from below.'
                  : 'Try another search, or select a Popular City below.'}
              </div>
            </TranslationProvider>
            <hr />
          </div>
        </AriaLiveRegion>
      );
    }
    return null;
  };

  const header = popular ? (
    <TranslationProvider translationKey="locations_header_popular">
      <h2 className="CitiesList__header">
        Popular Cities
      </h2>
    </TranslationProvider>
  ) : (
    <TranslationProvider translationKey="locations_header_search_results">
      <h2 className="CitiesList__header">
        Locations
      </h2>
    </TranslationProvider>
  );

  const getAriaLiveRegionResultMessage = () => {
    if (popular) {
      return null;
    }

    const numResults = activeLocations ? activeLocations.length : 0;

    const t = (numResults) ? getTranslation({
      translationKey: 'locations_search_aria_label',
      translationValues: { num: numResults },
      fallback: `There are ${numResults} results from your query. Use the up and down arrows to navigate results.`,
    }) : getTranslation({ translationKey: 'search_aria_label_no_results', fallback: 'No results.' });

    return t;
  };

  const getIsActive = (locationName) => locationName.toLowerCase() === selectedLocationName.toLowerCase();

  const renderSearchResults = () => (
    <ul
      role="listbox"
      id="cities-list"
      className="CitiesList__list"
      onBlur={onListBlur}
      tabIndex="-1"
    >
      {activeLocations.map((location) => (
        <li
          role="option"
          aria-selected={state && state[location.id] ? state[location.id] : 'false'}
          tabIndex="-1"
          key={location.id}
          id={location.id}
          onFocus={onItemFocus}
          onKeyDown={(e) => onItemKeyDown(e, location)}
          onClick={() => onLocationSelect(location)}
          className={cx('CitiesList__item', {
            active: getIsActive(location.name),
          })}
        >
          {location.name}
        </li>
      ))}
    </ul>
  );

  const renderPopularCities = () => (
    <ul
      className="CitiesList__list"
    >
      {activeLocations.map((location) => (
        <li
          key={location.id}
          className="CitiesList__item"
        >
          <Button
            variant="no-style"
            className={cx('CitiesList__anchor', {
              active: getIsActive(location.name),
            })}
            aria-current={getIsActive(location.name)}
            onClick={() => onLocationSelect(location)}
          >
            {location.name}
          </Button>
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={cx('CitiesList', {
        CitiesList__popular: popular,
        CitiesList__allCities: allCities,
        CitiesList__noHeader: hasTabs,
      })}
    >
      {errorMsg()}
      <AriaLiveRegion>{getAriaLiveRegionResultMessage()}</AriaLiveRegion>
      {hasTabs ? (
        <SimpleTabs
          activeTab={activeTab}
          ariaLabel={tabsAriaLabel}
          className="CitiesList__tabs"
          onTabChange={onTabChange}
          tabClassName="CitiesList__tab"
          tabActiveClassName="CitiesList__tab--active"
          tabs={tabs.map(({ id, label }) => ({ id, label }))}
        />
      ) : (
        header
      )}
      {popular ? (
        renderPopularCities()
      ) : (
        renderSearchResults()
      )}
    </div>
  );
};

CitiesList.propTypes = {
  allCities: PropTypes.bool,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  popular: PropTypes.bool,
  selectedLocationName: PropTypes.string,
  searchVal: PropTypes.string,
  onLocationSelect: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    locations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
  })),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  tabsAriaLabel: PropTypes.string,
  showWineRegions: PropTypes.bool,
};
CitiesList.defaultProps = {
  allCities: false,
  locations: [],
  popular: true,
  selectedLocationName: '',
  searchVal: null,
  onLocationSelect: () => {},
  tabs: null,
  activeTab: undefined,
  onTabChange: () => {},
  tabsAriaLabel: 'Tabs',
  showWineRegions: false,
};

CitiesList.displayName = 'CitiesList';
export default CitiesList;
